import Grid from "@mui/material/Grid";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { matchSorter } from "match-sorter";
import { SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm, UseFormGetValues } from "react-hook-form";
import { NavigateFunction, useNavigate } from "react-router";

import {
  AutoComplete,
  BcbsGridBody,
  ButtonGrid,
  ButtonStyle,
  EligibilityButtonGrid,
  EligibilityCheckGridBody,
  FaCheckCircleIcon,
  FaExclamationCircleIcon,
  GridBody,
  GridContainerField,
  HeaderTitle,
  HorizontalDivider,
  MainTitle,
  OuterGrid,
  StyledPopperCheckBoxAutocomplete,
  TopGrid,
} from "../../styles/style";
import fontWeight from "../../../../styles/mui/fontWeight";
import { colors } from "../../../../styles/colors";
import ReferralRequestTimeline from "./ReferralRequestTimeline";
import { FormInputText } from "../../../../components/formComponents/FormInputText";
import { rules } from "../../../../utils/validations/Validations";
import DatePicker from "../../../../components/formComponents/DatePicker";
import { displayFlex } from "../../../../styles/mui/styles/display";
import useMenuItem from "../../../../hooks/useMenuItem";
import {
  CheckEligibility,
  EligibilityCheckData,
  EligibilityInformation,
  EligibilityPayload,
  PatientFilterOptions,
} from "../../../../models/CheckEligibility";
import { NO_RECORD_FOUND } from "../../../../constants/tostmsg";
import {
  getHealthPlanValueType,
  getList,
  getValue,
  isEligible,
  isNil,
} from "../../../../utils";
import {
  eligibilityFormInitialState,
  EligibilityStatus,
  HealthPlanData,
} from "../../../../constants/ReferralRequestor";
import ReadOnlyField from "../../../../components/formComponents/ReadOnlyField";
import { PayerResponse } from "../../../../models/Api/Master";
import { CheckEligibilityResponse } from "../../../../models/Api/CheckEligibility";
import { DateFormats } from "../../../../constants/Date";
import { formatDate } from "../../../../utils/DateTime";
import {
  PRIOR_AUTH_WORKFLOW,
  WELCOME_PAGE,
} from "../../../../constants/ReferralPaths";
import { ReferralRequestor } from "../../../../models/ReferralRequestor";
import { isBcbsRepresentativeQuestion } from "../../../../constants/AllPatientRecord";
import { EligibilityType, IS_BCBS_MI_AUTH, PayerIdInfo } from "../../../../constants/MemberDetails";
import { DropDownType } from "../../../../models/MemberDetails";
import { BoxStyle, RadioGroupStyle } from "../memberDetails/style";

export interface PropsFromState {
  payerState: PayerResponse;
  checkEligibilityData: CheckEligibilityResponse;
  ReferralRequestor: ReferralRequestor;
}
export interface PropsFromDispatch {
  getMasterEligibilityPayer: () => void;
  checkEligibility: (payload: EligibilityPayload) => void;
  saveEligibilityInformation: (sessionData: EligibilityInformation) => void;
  saveReferralRequestor: (sessionData: ReferralRequestor) => void;
}

type Props = PropsFromState & PropsFromDispatch;

const EligibilityCheck = ({
  payerState,
  checkEligibilityData,
  getMasterEligibilityPayer,
  checkEligibility,
  saveEligibilityInformation,
  ReferralRequestor,
  saveReferralRequestor,
}: Props) => {
  const navigate = useNavigate();
  const [payer, setPayer] = useState<PatientFilterOptions[]>([HealthPlanData]);
  const { open, onOpen, onClose } = useMenuItem();
  const [healthPlan, setHealthPlan] = useState<PatientFilterOptions | null>(
    HealthPlanData
  );
  const [eligibilityData, setEligibilityData] = useState<
    CheckEligibility | null | undefined
  >(null);
  const [openEligibilityCheck, setOpenEligibilityCheck] = useState(false);
  const [isBcbs, setIsBcbs] = useState<boolean>(false);
  const [actionType, setActionType] = useState<"CHECK" | "PROCEED" | null>(null);

  useEffect(() => {
    if (!checkEligibilityData.loading) {
      setEligibilityData(checkEligibilityData.response);
      setOpenEligibilityCheck(true);
    }
  }, [checkEligibilityData]);
  useEffect(() => {
    const payerRespData = payerState.response.map((data) => {
      return {
        payerName: data.payerName,
        payerId: btoa(`${data.payerId}|||${data.payerName}`),
      };
    });
    setPayer(
      getList(payerRespData, {
        label: "payerName",
        value: "payerId",
      })
    );
  }, [payerState.response]);

  useEffect(() => {
    if (
      payerState &&
      !payerState.loading &&
      getValue(payerState, "response", []).length === 0
    ) {
      getMasterEligibilityPayer();
    }
  }, []);

  const methods = useForm<any>({
    defaultValues: eligibilityFormInitialState,
    mode: "all",
  });

  const { control, setValue, clearErrors, getValues, handleSubmit, reset } =
    methods;
  const values = getValues();

  const handleKeyDown = (event: any) => {
    if (event.key === " " && !event.target.value.trim()) {
      event.preventDefault();
    }
  };

  const onHealthPlanSelection = (
    _e: SyntheticEvent<Element, Event>,
    value: any
  ) => {
    onClose();
    setHealthPlan(value);
    setValue("healthPlan", value.value);
    clearErrors("healthPlan");

    const bcbs = getHealthPlanValueType(getValues("healthPlan"), 0) === PayerIdInfo.BCBS_MICHIGAN_PAYERID;
    setIsBcbs(bcbs);
  };

  const handleCheckEligibility = () => {
    const payload = {
      lastName: getValues("lastName"),
      dob: getValues("dateOfBirth")
        ? formatDate(getValues("dateOfBirth"), DateFormats.YYYY_MM_DD)
        : "",
      policyId: getValues("subscriberId"),
      payerId: getValues("healthPlan")
        ? getHealthPlanValueType(getValues("healthPlan"), 0)
        : "",
      payerName: getValues("healthPlan")
        ? getHealthPlanValueType(getValues("healthPlan"), 1)
        : "",
    };

    checkEligibility(payload);
  };

  const handleReset = () => {
    setOpenEligibilityCheck(false);
    setHealthPlan(null);
    reset({ values, keepDefaultValue: true });
    setIsBcbs(false);
  };

  useEffect(() => {
    sessionStorage.removeItem("ReferralRequest");
    sessionStorage.removeItem("checkEligibilityResponse");
    const requestorInformation = sessionStorage.getItem("requestorInformation");
    if (isNil(requestorInformation)) {
      sessionStorage.clear();
      navigate(WELCOME_PAGE);
    }
    if (
      !isNil(requestorInformation) &&
      getValue(ReferralRequestor, "firstName").length === 0
    ) {
      const requestorData = JSON.parse(requestorInformation as string);
      saveReferralRequestor(requestorData);
    }
    setOpenEligibilityCheck(false);
  }, []);

  const onRadioChange = (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setValue(e.target.name, value, { shouldValidate: true });
  }
 
  return (
    <Box sx={OuterGrid}>
      <Grid container>
        <Grid size={12} sx={TopGrid}>
          <Typography
            variant="h3"
            fontWeight={fontWeight.Weight[6]}
            color={colors.black[2]}
          >
            Prior Authorization Form
          </Typography>
        </Grid>
        <Grid size={12} sx={TopGrid}>
          <ReferralRequestTimeline />
        </Grid>
        <Grid size={12}>
          <Grid container sx={GridBody}>
            <Grid size={12}>
              <Typography
                variant="subtitle1"
                color={colors.fonts[20]}
                fontWeight={fontWeight.Weight[5]}
              >
                MEMBER DETAILS
              </Typography>
              <Typography
                variant="subtitle2"
                fontWeight={fontWeight.Weight[2]}
                color={colors.black[2]}
                pt={"0.3rem"}
              >
                Please enter member details to check insurance eligibility.
              </Typography>
              <Divider sx={HorizontalDivider} />
              <Grid size={12} mt={"2rem"}>
                <Grid container>
                  <Grid size={1.5} pr={"2rem"}>
                    <FormInputText
                      name="lastName"
                      label="Last Name:"
                      control={control}
                      helper={rules.eligibilityFirstName}
                    />
                  </Grid>
                  <Grid size={1.5} pr={"2rem"}>
                    <FormInputText
                      name="firstName"
                      label="First Name:"
                      control={control}
                      helper={rules.patientFirstName}
                    />
                  </Grid>
                  <Grid size={1.5} pr={"2rem"}>
                    <FormControl variant="standard">
                      <DatePicker
                        name="dateOfBirth"
                        control={control}
                        label={"Date of Birth:"}
                        helper={rules.dateOfBirth}
                        allowFutureDate={false}
                      />
                    </FormControl>
                  </Grid>
                  <Grid size={1.5} pr={"2rem"}>
                    <FormInputText
                      name="subscriberId"
                      label="Subscriber ID:"
                      control={control}
                      helper={rules.checkEligibilitySubscriberId}
                    />
                  </Grid>
                  <Grid size={2} mt={"-0.2rem"}>
                    <Box sx={displayFlex}>
                      <Typography
                        variant="body1"
                        fontWeight={fontWeight.Weight[2]}
                        color={colors.fonts[2]}
                      >
                        Health Plan:
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        fontWeight={fontWeight.Weight[2]}
                        color={colors.red[100]}
                        pl={"0.3rem"}
                      >
                        *
                      </Typography>
                    </Box>
                    <Controller
                      render={({ fieldState: { error } }) => (
                        <>
                          <Autocomplete
                            sx={AutoComplete}
                            options={payer}
                            freeSolo
                            forcePopupIcon
                            getOptionLabel={(
                              option: PatientFilterOptions | string
                            ) =>
                              typeof option === "string" ? option : option.label
                            }
                            open={open}
                            onOpen={() => onOpen()}
                            onClose={() => onClose()}
                            slots={{ popper: StyledPopperCheckBoxAutocomplete }}
                            filterOptions={(options: any, { inputValue }) =>
                              matchSorter(options, inputValue, {
                                keys: ["label"],
                              })
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.label === value.label
                            }
                            id="auto-complete"
                            autoComplete
                            disableClearable
                            clearIcon={""}
                            onChange={onHealthPlanSelection}
                            includeInputInList
                            value={healthPlan ? healthPlan : undefined}
                            noOptionsText={NO_RECORD_FOUND}
                            data-testid="health-plan"
                            renderInput={(params: any) => {
                              params.inputProps.maxLength = 50;
                              return (
                                <Box>
                                  <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder="Choose"
                                    onKeyDown={handleKeyDown}
                                    slotProps={{
                                      input: {
                                        ...params.InputProps,
                                      },
                                    }}
                                  />
                                </Box>
                              );
                            }}
                            renderOption={(props, option) => {
                              const { key, ...Itemprops } = props;
                              return (
                                <Typography
                                  key={key}
                                  {...Itemprops}
                                  variant="body1"
                                  fontWeight={fontWeight.Weight[4]}
                                  color={colors.fonts[25]}
                                >
                                  {option.label}
                                </Typography>
                              );
                            }}
                          />
                          {error && (
                            <FormHelperText>{error.message}</FormHelperText>
                          )}
                        </>
                      )}
                      control={control}
                      name="healthPlan"
                      rules={rules.checkEligibilityHealthPlan}
                    />
                  </Grid>
                  <Grid size={4} sx={ButtonGrid}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setActionType(EligibilityType.ELIGIBILITY_CHECK);
                        handleSubmit(handleCheckEligibility)();
                      }}
                      sx={ButtonStyle}
                    >
                      Check Eligibility
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleReset}
                      sx={ButtonStyle}
                    >
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {isBcbs && (<Grid size={12} mb={"1rem"}>
          <Grid container sx={BcbsGridBody}>
            <Grid size={"auto"} sx={MainTitle} mr={"1.5rem"} pt={"1.5rem"}>
              <Typography
                variant="subtitle1"
                color={colors.fonts[20]}
                fontWeight={fontWeight.Weight[5]}
              >
                {isBcbsRepresentativeQuestion}
              </Typography>
            </Grid>
            <Grid size={"auto"} pt={"0.5rem"}>
              <Controller
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <Box>
                    <>
                      <RadioGroup
                        aria-labelledby="controlled-radio-buttons-group"
                        sx={RadioGroupStyle}
                        {...field}
                        onChange={onRadioChange}
                      >
                        {IS_BCBS_MI_AUTH.map(
                          (singleOption: DropDownType) => (
                            <Box key={singleOption.value} sx={BoxStyle}>
                              <FormControlLabel
                                value={singleOption.value}
                                label={
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={fontWeight.Weight[5]}
                                    color={colors.black[5]}
                                  >
                                    {singleOption.label}
                                  </Typography>
                                }
                                control={<Radio color="primary" />}
                              />
                            </Box>
                          )
                        )}
                      </RadioGroup>
                      <FormHelperText>
                        {error ? error.message : null}
                      </FormHelperText>
                    </>
                  </Box>
                )}
                name={`response`}
                control={control}
                rules={{
                  validate: (value) => {
                    if (isBcbs && actionType === EligibilityType.PROCEED_TO_REQUEST && !value) {
                      return "Please select an option";
                    }
                    return true;
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>)} 
        {(getValue(eligibilityData, "eligibilityStatus", "") ==
          EligibilityStatus.STATUS_TRUE ||
          getValue(eligibilityData, "eligibilityStatus", "") ==
            EligibilityStatus.STATUS_TRUE_WITHOUT_SPACE) &&
          openEligibilityCheck && (
            <EligibilityCheckSuccess
              eligibilityData={values}
              eligibilityResponse={eligibilityData}
              navigate={navigate}
              saveEligibilityInformation={saveEligibilityInformation}
            isBcbs={isBcbs}
            setActionType={setActionType}
            handleSubmit={handleSubmit}
            getValues={getValues}
            />
          )}
        {eligibilityData &&
          !isEligible(eligibilityData) &&
          openEligibilityCheck && (
            <EligibilityCheckFail
              eligibilityData={values}
              eligibilityResponse={eligibilityData}
              navigate={navigate}
              saveEligibilityInformation={saveEligibilityInformation}
            isBcbs={isBcbs}
            setActionType={setActionType}
            handleSubmit={handleSubmit}
            getValues={getValues}
            />
          )}
      </Grid>
    </Box>
  );
};

type EligibilityCheckSuccess = {
  eligibilityData: EligibilityCheckData;
  eligibilityResponse: CheckEligibility | null | undefined;
  saveEligibilityInformation: (sessionData: EligibilityInformation) => void;
  navigate: NavigateFunction;
  isBcbs: boolean;
  setActionType: React.Dispatch<React.SetStateAction<"CHECK" | "PROCEED" | null>>;
  handleSubmit: any;
  getValues: UseFormGetValues<any>;
};

const EligibilityCheckSuccess = ({
  eligibilityData,
  eligibilityResponse,
  saveEligibilityInformation,
  navigate,
  isBcbs,
  setActionType,
  handleSubmit,
  getValues
}: EligibilityCheckSuccess) => {
  const handleCreateRequest = () => {
    if (eligibilityData && eligibilityResponse) {
      const checkEligibilityResponse = {
        lastName: eligibilityData.lastName,
        dob: getValue(eligibilityData, "dateOfBirth")
          ? formatDate(eligibilityData.dateOfBirth, DateFormats.YYYY_MM_DD)
          : "",
        policyId: eligibilityData.subscriberId,
        payerId:
          eligibilityData.healthPlan
            ? getHealthPlanValueType(eligibilityData.healthPlan, 0)
            : "",
        payerName:
          eligibilityData.healthPlan
            ? getHealthPlanValueType(eligibilityData.healthPlan, 1)
            : "",
        firstName: eligibilityData.firstName,
        eligibilityId: getValue(eligibilityResponse, "eligibilityId", ""),
        eligibilityStatus: getValue(
          eligibilityResponse,
          "eligibilityStatus",
          ""
        ),
        response: isBcbs ? getValues("response") : null,
        isBcbsmiAuth: isBcbs ? isBcbsRepresentativeQuestion : null
      };
      saveEligibilityInformation(checkEligibilityResponse);
    }

    navigate(PRIOR_AUTH_WORKFLOW);
  };

  return (
    <>
      <Grid size={12}>
        <Grid container sx={EligibilityCheckGridBody}>
          <Grid size={12} sx={{ display: "flex" }}>
            <FaCheckCircleIcon />
            <Box sx={HeaderTitle}>
              <Typography
                variant="subtitle1"
                color={colors.fonts[20]}
                fontWeight={fontWeight.Weight[5]}
              >
                Referral with below details has been successfully verified,
                please proceed with adding rest of the data.
              </Typography>
            </Box>
          </Grid>
          <Grid size={12}>
            <Grid container sx={GridContainerField}>
              <Grid size={1.5}>
                <ReadOnlyField
                  label={"Last Name:"}
                  value={eligibilityData.lastName}
                />
              </Grid>
              <Grid size={1.5}>
                <ReadOnlyField
                  label={"First Name:"}
                  value={eligibilityData.firstName}
                />
              </Grid>
              <Grid size={1.5}>
                <ReadOnlyField
                  label={"Date of birth:"}
                  value={formatDate(
                    eligibilityData.dateOfBirth,
                    DateFormats.MM_DD_YYYY
                  )}
                />
              </Grid>
              <Grid size={1.5}>
                <ReadOnlyField
                  label={"Subscriber ID:"}
                  value={eligibilityData.subscriberId}
                />
              </Grid>
              <Grid size={1.5}>
                <ReadOnlyField
                  label={"Health Plan:"}
                  value={getHealthPlanValueType(eligibilityData.healthPlan, 1)}
                />
              </Grid>
              <Grid size={4.5} sx={EligibilityButtonGrid}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setActionType(EligibilityType.PROCEED_TO_REQUEST);
                    handleSubmit(handleCreateRequest)();
                  }}
                  sx={ButtonStyle}
                >
                  Create Request
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

type EligibilityCheckFailType = {
  eligibilityData: EligibilityCheckData;
  eligibilityResponse: CheckEligibility | null | undefined;
  saveEligibilityInformation: (sessionData: EligibilityInformation) => void;
  navigate: NavigateFunction;
  isBcbs: boolean;
  setActionType: React.Dispatch<React.SetStateAction<"CHECK" | "PROCEED" | null>>;
  handleSubmit: any;
  getValues: UseFormGetValues<any>;
};
const EligibilityCheckFail = ({
  eligibilityData,
  eligibilityResponse,
  saveEligibilityInformation,
  navigate,
  isBcbs,
  setActionType,
  handleSubmit,
  getValues,
}: EligibilityCheckFailType) => {
  const handleAddManually = () => {
    if (eligibilityData && eligibilityResponse) {
      const checkEligibilityResponse = {
        lastName: eligibilityData.lastName,
        dob: getValue(eligibilityData, "dateOfBirth")
          ? formatDate(eligibilityData.dateOfBirth, DateFormats.YYYY_MM_DD)
          : "",
        policyId: eligibilityData.subscriberId,
        payerId:
          eligibilityData.healthPlan
            ? getHealthPlanValueType(eligibilityData.healthPlan, 0)
            : "",
        payerName:
          eligibilityData.healthPlan
            ? getHealthPlanValueType(eligibilityData.healthPlan, 1)
            : "",
        firstName: eligibilityData.firstName,
        eligibilityId: getValue(eligibilityResponse, "eligibilityId", ""),
        eligibilityStatus: getValue(
          eligibilityResponse,
          "eligibilityStatus",
          ""
        ),
        response: isBcbs ? getValues("response") : null,
        isBcbsmiAuth: isBcbs ? isBcbsRepresentativeQuestion : null
      };
      saveEligibilityInformation(checkEligibilityResponse);
      sessionStorage.setItem(
        "checkEligibilityResponse",
        JSON.stringify(checkEligibilityResponse)
      );
    }

    navigate(PRIOR_AUTH_WORKFLOW);
  };
  return (
    <>
      <Grid size={12}>
        <Grid container sx={EligibilityCheckGridBody}>
          <Grid size={12} sx={{ display: "flex" }}>
            <FaExclamationCircleIcon />
            <Box sx={HeaderTitle}>
              <Typography
                variant="subtitle1"
                color={colors.fonts[20]}
                fontWeight={fontWeight.Weight[5]}
              >
                {`Referral with below details couldn't been verified / Not - Eligible`}
              </Typography>
            </Box>
          </Grid>
          <Grid size={12}>
            <Grid container sx={GridContainerField}>
              <Grid size={1.5}>
                <ReadOnlyField
                  label={"Member Name:"}
                  value={`${eligibilityData.firstName} ${eligibilityData.lastName}`}
                />
              </Grid>
              <Grid size={1.5}>
                <ReadOnlyField
                  label={"Date of birth:"}
                  value={formatDate(
                    eligibilityData.dateOfBirth,
                    DateFormats.MM_DD_YYYY
                  )}
                />
              </Grid>
              <Grid size={1.5}>
                <ReadOnlyField
                  label={"Subscriber ID:"}
                  value={eligibilityData.subscriberId}
                />
              </Grid>
              <Grid size={1.5}>
                <ReadOnlyField
                  label={"Health Plan:"}
                  value={getHealthPlanValueType(eligibilityData.healthPlan, 1)}
                />
              </Grid>
              <Grid size={6} sx={EligibilityButtonGrid}>
                <Button variant="contained"
                  onClick={() => {
                    setActionType(EligibilityType.PROCEED_TO_REQUEST);
                    handleSubmit(handleAddManually)();
                  }}
                >
                  Add Details Manually
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default EligibilityCheck;
